"use strict";

import {
    onAuthStateChanged,
    signInWithEmailAndPassword,
    signOut,
} from "firebase/auth";

import apiClient from "../../utils/apiClient.js";
import { RESET_PASSWORD, SIGN_IN_USER, SIGN_UP_USER } from "../../ApiEndpoints";
import { auth } from "../../config/firebase.js";
import {
    handleSessionExpired,
    isSessionEndingAuthError,
    SESSION_EXPIRED_MESSAGE,
} from "./session.js";

export const signInUser = (payload) => apiClient.post(SIGN_IN_USER, payload);

export const signUpUser = (payload) => apiClient.post(SIGN_UP_USER, payload);

export const resetPassword = (payload) => apiClient.post(RESET_PASSWORD, payload);

// Keep getAuth()'s default persistence (IndexedDB, falling back to localStorage); it already
// survives restarts. Switching persistence here made other tabs migrate the user back and
// delete this tab's copy, signing it out whenever another tab of the site loaded.
export const signInFirebaseUser = ({ email, password }) => signInWithEmailAndPassword(
    auth,
    email,
    password
);

export const signOutFirebaseUser = async () => {
    if (auth.currentUser) {
        await signOut(auth);
    }
};

const waitForFirebaseAuthState = () => new Promise((resolve, reject) => {
    let unsubscribe = () => {};

    unsubscribe = onAuthStateChanged(
        auth,
        (user) => {
            unsubscribe();
            resolve(user);
        },
        (error) => {
            unsubscribe();
            reject(error);
        }
    );
});

// Callers that ask at the same time share one read. Once the cached token has expired, every
// getIdToken() call sends its own refresh to securetoken.googleapis.com, so a page mounting
// several media players would otherwise refresh once per player.
let pendingIdToken = null;

export const getFirebaseIdToken = () => {
    if (!pendingIdToken) {
        pendingIdToken = readFirebaseIdToken().finally(() => {
            pendingIdToken = null;
        });
    }
    return pendingIdToken;
};

const readFirebaseIdToken = async () => {
    const firebaseUser = auth.currentUser || await waitForFirebaseAuthState();

    if (!firebaseUser) {
        handleSessionExpired("firebase-user-missing");
        throw new Error(SESSION_EXPIRED_MESSAGE);
    }

    try {
        return await firebaseUser.getIdToken();
    } catch (error) {
        if (isSessionEndingAuthError(error)) {
            handleSessionExpired(`firebase-token-error:${error.code}`);
        }
        throw error;
    }
};
